import {
  ApiClient,
  CoreAPITypes,
  SphereDashboardAPITypes,
} from "@stellar/api-logic";
import { assertValue } from "@utils/assert-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { CompanyId } from "@custom-types/base-types";

// ------   Mocks   ---------------------------------------------------------------------------------------------------------
// These mocked API responses are used when accessing a project of a foreign workspace with ?enableDev=true and HbAdmin role.

interface DevModeMocks {
  /** Mock response for: GET /v3/company/:companyId/features */
  features: CoreAPITypes.IFeatureStateResponse[];
  /** Mock response for: GET /v3/company/:companyId/context */
  companyContext: SphereDashboardAPITypes.CompanyContext;
  /** Mock response for: GET /v3/dashboard/company/:companyId/member?types=user%2Cuser-group%2Cuser-invite */
  members: SphereDashboardAPITypes.ICompanyMemberBase[];
  /** Mock response for: GET /v3/company/:companyId/user-groups */
  teams: SphereDashboardAPITypes.IPaginationResponse<
    SphereDashboardAPITypes.ITeam[]
  >;
}

/**
 * Mocked API responses when accessing a project of a foreign workspace with ?enableDev=true and HbAdmin role.
 * Currently it seems not required to include any special entries, empty arrays are enough.
 */
export const devModeMocks: DevModeMocks = {
  features: [],
  companyContext: {
    featuresAvailable: [],
    userRoles: [],
    // trial: Not needed for our purposes.
  },

  members: [],
  teams: {
    data: [],
    // "next" is needed to satisfy the IPaginationResponse type, but would not be included in a real API response
    // if the result fits on one page.
    next: "",
  },
};

// -----------------------------------------------------------------------------------------------

/**
 *  @returns The context of the given company, or fallback values if (error && devMode),
 *           for usage with HbAdmin role.
 */
export function getCompanyContext(
  coreApiClient: ApiClient,
  companyId: string,
  isDevModeEnabled: boolean
): Promise<SphereDashboardAPITypes.CompanyContext> {
  return coreApiClient.V3.SDB.getCompanyContext(companyId).catch((error) => {
    if (isDevModeEnabled) {
      return devModeMocks.companyContext;
    } else {
      throw error;
    }
  });
}

/**
 * @returns The user profile for the given company, or for another company if (error && devMode),
 *          for usage with HbAdmin role.
 */
export async function getCurrentUserProfileInfos(
  companyId: CompanyId,
  isDevModeEnabled: boolean
): Promise<FaroCoreApiTypes.CompanyMemberBase> {
  try {
    const data = await faroCoreApiClient.users.getCurrentUserProfileInfos(
      companyId
    );
    return data;
  } catch (error) {
    if (isDevModeEnabled) {
      try {
        // The user profile can be obtained from any company where the user is member of.
        const companies = await faroCoreApiClient.company.getCompanies();
        const userOtherCompany =
          await faroCoreApiClient.users.getCurrentUserProfileInfos(
            assertValue(companies[0].id)
          );
        // Take user-specific values only, and hard-code company-specific ones.
        return {
          email: userOtherCompany.email,
          firstName: userOtherCompany.firstName,
          lastName: userOtherCompany.lastName,
          id: userOtherCompany.id,
          identity: userOtherCompany.identity,
          permissions: [],
          role: "COMPANY_VIEWER",
        };
      } catch (errorFallback) {
        // Log the extra error, and then throw the original error below.
        // eslint-disable-next-line no-console -- Developer feature
        console.error("Error with fallback company", errorFallback);
      }
    }
    throw error;
  }
}

/**
 * It's required to adapt the user's search input before passing it to any CoreAPI endpoint
 * that supports search query. The following adaptations are required:
 * - If the string is empty then set the value as undefined. Otherwise passing an empty string
 * as search query might cause unexpected results.
 * @param search User's input
 * @returns The adapted search input
 */
export function adaptSearch(search: string | undefined): string | undefined {
  return search?.length ? search : undefined;
}
